import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Handshake, Users, Workflow, Landmark, Sprout, Wheat, Building2, HeartPulse } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { DnaHelix } from "@/components/DnaHelix";
import { SEO } from "@/components/SEO";
import { VALUES, LEADERS, TIMELINE, INDUSTRIES } from "@/data/content";

const ICONS = { Handshake, Users, Workflow, Landmark };

const AboutHero = () => (
    <section className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden bg-[#0A0D20] pb-16 pt-36 md:pb-20 md:pt-44" data-testid="about-hero">
        <div
            className="absolute inset-0 opacity-25"
            style={{
                backgroundImage: `url(${INDUSTRIES[2].image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D20] via-[#0A0D20]/70 to-[#0A0D20]/80" />
        <DnaHelix className="pointer-events-none absolute right-4 top-1/2 hidden h-[540px] w-[540px] -translate-y-1/2 opacity-80 lg:block xl:right-16" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
            <Reveal>
                <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#F16622]">
                    <span className="inline-block h-px w-10 bg-[#F16622]" />
                    About MS Group
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h1 className="font-display mt-7 max-w-4xl text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl">
                    An Indian group, built for generations.
                </h1>
            </Reveal>
            <Reveal delay={0.2}>
                <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
                    From a single seed company in 2014 to a diversified group across hybrid seeds, FMCG, real estate
                    and healthcare — this is the story of MS Group.
                </p>
            </Reveal>
        </div>
    </section>
);

const Story = () => (
    <section className="bg-white py-14 md:py-20" data-testid="about-story">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-6 md:px-12 lg:grid-cols-2 lg:px-20">
            <div>
                <SectionHead overline="Our Story" title="It began with a seed — and a promise." />
                <Reveal delay={0.15}>
                    <div className="mt-8 space-y-6 text-base leading-relaxed text-slate-600">
                        <p>
                            Established in 2014, MS Group began with a stronghold in India's seed industry. Over a
                            decade, that single conviction grew into four seed companies serving over a million farmer
                            families across Pan India — supported by hundreds of employees and a dealer network that
                            reaches the country's farthest villages.
                        </p>
                        <p>
                            Today, we are stepping into new horizons — healthcare, FMCG and real estate — carrying the
                            same passion, purpose and people-first approach that defined our roots. As a diversified
                            group, we are driven by long-term value, regional impact, and a vision to build
                            future-ready businesses across India.
                        </p>
                        <p>
                            Agriculture created our foundation. Diversification represents our future. And trust — the
                            kind earned season after season — remains our only strategy.
                        </p>
                    </div>
                </Reveal>
            </div>
            <Reveal delay={0.1} className="relative">
                <div className="relative ml-auto w-full max-w-[480px]">
                    <div className="absolute -left-5 -top-5 h-full w-full border border-[#F16622]" />
                    <div className="grain relative overflow-hidden bg-[#0A0D20] p-10 md:p-12" data-testid="about-story-panel">
                        <div
                            className="pointer-events-none absolute inset-0"
                            style={{ background: "radial-gradient(ellipse 80% 55% at 85% 0%, rgba(241,102,34,0.16), transparent 65%)" }}
                        />
                        <div className="relative">
                            <div className="flex items-center justify-between">
                                <img src="/assets/favicon-icon.png" alt="MS Group mark" className="h-12 w-12 object-contain" />
                                <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">
                                    The Group Today
                                </span>
                            </div>
                            <p className="font-display mt-10 text-3xl font-medium leading-snug text-white md:text-4xl">
                                From one seed to{" "}
                                <span className="text-[#F16622]">seven companies</span>.
                            </p>
                            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10">
                                {[
                                    { Icon: Sprout, name: "Hybrid Seeds", c: "#58A618" },
                                    { Icon: Wheat, name: "FMCG & Foods", c: "#F16622" },
                                    { Icon: Building2, name: "Real Estate", c: "#60A5FA" },
                                    { Icon: HeartPulse, name: "Healthcare", c: "#2EC4B6" },
                                ].map((s) => (
                                    <div key={s.name} className="flex items-center gap-3 bg-[#0A0D20] px-4 py-5">
                                        <s.Icon className="h-5 w-5 shrink-0" style={{ color: s.c }} strokeWidth={1.8} />
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">
                                            {s.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
                                <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                                    Est. 2014 · Hyderabad, India
                                </span>
                                <span className="font-display text-2xl font-medium text-[#F16622]">4 Industries</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

const Values = () => (
    <section className="bg-[#F8F7F3] py-14 md:py-20" data-testid="about-values">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
            <SectionHead
                overline="What We Stand For"
                title="Values that compound."
                sub="These aren't posters on a wall. They are the operating principles behind every acre, every product, every square foot and every prescription."
            />
            <div className="mt-16 grid gap-px bg-[#E7E5DE] sm:grid-cols-2 lg:grid-cols-4">
                {VALUES.map((v, i) => {
                    const Icon = ICONS[v.icon];
                    return (
                        <div key={v.title} className="group bg-[#F8F7F3] p-8 transition-colors duration-500 hover:bg-white md:p-10" data-testid={`value-${v.icon.toLowerCase()}`}>
                            <Reveal delay={i * 0.07}>
                                <Icon className="h-7 w-7 text-[#F16622]" strokeWidth={1.5} />
                                <h3 className="font-display mt-6 text-xl font-medium text-[#0F172A]">{v.title}</h3>
                                <p className="mt-4 text-sm leading-relaxed text-slate-600">{v.text}</p>
                            </Reveal>
                        </div>
                    );
                })}
            </div>
        </div>
    </section>
);

const LeaderProfile = ({ leader, flip }) => (
    <div
        className={`grid items-center gap-12 lg:grid-cols-2 ${flip ? "" : ""}`}
        data-testid={`about-leader-${leader.id}`}
    >
        <Reveal className={flip ? "lg:order-2" : ""}>
            <div className="relative mx-auto max-w-[480px]">
                <div className="absolute -right-5 -top-5 h-full w-full border border-[#F16622]" />
                <img src={leader.photo} alt={leader.name} loading="lazy" className="relative w-full object-cover object-top" />
            </div>
        </Reveal>
        <div className={flip ? "lg:order-1" : ""}>
            <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F16622]">{leader.role}</p>
                <h3 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0F172A] md:text-4xl">
                    {leader.name}
                </h3>
            </Reveal>
            <Reveal delay={0.1}>
                <blockquote className="mt-7 border-l-2 border-[#F16622] pl-6 text-lg font-light leading-relaxed text-slate-700">
                    &ldquo;{leader.quote}&rdquo;
                </blockquote>
            </Reveal>
            <Reveal delay={0.18}>
                <p className="mt-7 text-sm text-slate-500">{leader.credsText}</p>
                {leader.creds && (
                    <div className="mt-5 inline-block border border-[#ECEAE3] bg-white p-4">
                        <img src={leader.creds} alt={`${leader.name} — alma mater`} loading="lazy" className="max-h-16 w-auto object-contain" />
                    </div>
                )}
            </Reveal>
            <Reveal delay={0.24}>
                <a
                    href={leader.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`about-leader-linkedin-${leader.id}`}
                    className="group mt-8 inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#232B6B] transition-colors hover:text-[#F16622]"
                >
                    <Linkedin className="h-4 w-4" strokeWidth={1.5} />
                    Connect on LinkedIn
                </a>
            </Reveal>
        </div>
    </div>
);

export default function About() {
    return (
        <main data-testid="about-page">
            <SEO
                title="About Us | MS Group — Our Story, Leadership & Values"
                description="Founded in 2014 in Hyderabad, MS Group grew from a single farmer-first seed brand into a diversified group across seeds, foods, real estate and healthcare. Meet our leadership and our philosophy."
            />
            <AboutHero />
            <Story />
            <Values />

            {/* Work approach band */}
            <section className="bg-[#D9A521] py-12 md:py-16" data-testid="about-work-approach">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <Reveal>
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#0A0D20]/60">Our Work Approach</p>
                        <p className="font-display mt-5 max-w-4xl text-2xl font-medium leading-snug text-white md:text-4xl">
                            Great companies aren't run by chance — they're built on clarity, people, and repeatable
                            systems.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Journey condensed */}
            <section className="bg-white py-14 md:py-20" data-testid="about-journey">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <SectionHead overline="The Journey So Far" title="Ten years. Eight milestones. One direction." />
                    <div className="mt-16 grid gap-px bg-[#E7E5DE] sm:grid-cols-2 lg:grid-cols-4">
                        {TIMELINE.map((t, i) => (
                            <div key={t.year} className="group bg-white p-8 transition-colors duration-500 hover:bg-[#0A0D20]" data-testid={`about-timeline-${t.year}`}>
                                <Reveal delay={i * 0.05}>
                                    <div className="flex items-center gap-3">
                                        <p className="font-display text-3xl font-medium text-[#F16622]">{t.year}</p>
                                        {t.upcoming && (
                                            <span className="border border-[#F16622] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#F16622]" data-testid="about-timeline-upcoming-badge">
                                                Upcoming
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-display mt-4 text-lg font-medium text-[#0F172A] transition-colors duration-500 group-hover:text-white">
                                        {t.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600 transition-colors duration-500 group-hover:text-white/60">
                                        {t.text}
                                    </p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team & Leadership */}
            <section className="bg-[#F8F7F3] py-14 md:py-20" data-testid="about-leadership">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <SectionHead
                        overline="Team & Leadership"
                        title="Globally trained. Deeply rooted."
                        sub="The leadership of MS Group pairs education from the world's finest institutions with an uncompromising understanding of the Indian ground reality."
                    />
                    <div className="mt-16 space-y-20">
                        {LEADERS.map((l, i) => (
                            <LeaderProfile key={l.id} leader={l} flip={i % 2 === 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Vision CTA */}
            <section className="grain relative bg-[#0A0D20] py-14 md:py-20" data-testid="about-vision">
                <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <SectionHead
                        dark
                        overline="Our Ambition"
                        title="To be India's most trusted emerging business group."
                        sub="Trusted by farmers in the field, families at the table, homeowners on their land, and patients in our care. That is the measure of MS Group."
                    />
                    <Reveal delay={0.2}>
                        <Link
                            to="/group"
                            data-testid="about-explore-group-btn"
                            className="group mt-10 inline-flex items-center gap-3 bg-[#F16622] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-[#0A0D20]"
                        >
                            Explore the Group
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
